import React, { FunctionComponent, useCallback, useEffect, useRef } from 'react'
import styles from './ProjectPage.module.sass'
import ObjectHeader from './ObjectHeader/ObjectHeader'
import ProjectInfo from './ProjectInfo/ProjectInfo'
import { useParams } from 'react-router'
import { RequestStatus } from '../../../../responses/response'
import { LoadingSkeleton } from '../../../etc/loading-components'
import { EMPTY_PROJECT, Project, ProjectStatus } from '../../../../responses/projects'
import { useNavigation, useRootDispatch, useRootSelector, useAnyPermissions } from '../../../../utils/hooks'
import { inactiveContext, statusContext } from '../../../../constants/contexts'
import { objectsSelector, pipeSelectors, projectObjectsSelector, projectSelector } from '../../../../utils/selectors'
import ObjectPicker from '../../../data-components/pickers/ObjectPicker/ObjectPicker'
import Helmet from 'react-helmet'
import { projectActions } from '../../../../redux/data/project/project-actions'
import TopBarProgress from 'react-topbar-progress-indicator'
import Participants from './Participants/Participants'
import { objectActions } from '../../../../redux/data/object/object-actions'
import Button, { ButtonStyle } from '../../../controls/Button/Button'
import { HasAnyPermission } from '../../../etc/HasPermission'
import { PERMISSIONS } from '../../../../constants/permissions'
import ObjectCloseDialog from '../../../data-components/dialogs/ObjectCloseDialog/ObjectCloseDialog'
import { flow } from 'fp-ts/es6/function'
import { isEmpty } from 'fp-ts/es6/Array'
import { NumberParam, useQueryParam } from 'use-query-params'
import { push } from 'connected-react-router'
import { urlWithParams } from '../../../../http/utils'
import { ROUTES } from '../../../../http/routes'
import PlusIcon from 'files/plus.svg'
import ObjectBody from './ObjectBody/ObjectBody'
import { useQueries } from '../../../../utils/hooks/useQueries'
import { projectDetailActions } from '../../../../redux/pages/project-detail'
import { ObjectEditState } from '../../../../redux/data/object/object-reducer'
import { enumIs } from '../../../../utils/enum-utils'
import { Colors } from '../../../../styles'
import NotFound from '../../../etc/NotFound/NotFound'

export const isArchivedProject = enumIs<ProjectStatus>()([ProjectStatus.ARCHIVED, ProjectStatus.RESERVED])

const oldCount = [0]

const ProjectPage: FunctionComponent = () => {
    const {projectId} = useParams<{ projectId: string }>()
    const dispatch = useRootDispatch()
    const state = useRootSelector(state => state.pages.projectDetail.state)
    const openObjects = useRootSelector(state => state.pages.projectDetail.openObjects)
    const objectEditState = useRootSelector(state => state.data.object.editState)
    const project: Project = useRootSelector(projectSelector(projectId)) || EMPTY_PROJECT
    const projectWorkObjects = useRootSelector(pipeSelectors(project.id, projectObjectsSelector, objectsSelector))
    const {isDesktop} = useQueries()

    const objectEdit = ObjectEditState.is.create(objectEditState) && objectEditState.projectId == projectId

    const [queryTask] = useQueryParam('task', NumberParam)

    useEffect(() => {
        if (queryTask) {
            dispatch(push(urlWithParams({taskId: queryTask})(ROUTES.TASK)))
        }
    }, [dispatch, queryTask])

    useEffect(() => {
        dispatch(projectActions.get.request(projectId))
    }, [dispatch, projectId])

    const toggleObjects = useCallback((value: boolean) => dispatch(value ?
        objectActions.startCreate(projectId) :
        objectActions.cancelEdit()
    ), [dispatch, projectId])

    const mainRef = useRef(null)
    useNavigation(mainRef)

    useEffect(() => { // eslint-disable-line
        if(projectWorkObjects.length > 0) {
            if (oldCount[oldCount.length - 1] !== projectWorkObjects.length) {
                oldCount.push(projectWorkObjects.length)
                projectWorkObjects.map(object => {
                    if(object.tasks_count === 0) {
                        openObjects[object.id] !== true ? dispatch(projectDetailActions.toggleObject(object.id)) : ''
                    }
                })
            }
        }

    }, [projectWorkObjects]) // eslint-disable-line

    const isPermit = useAnyPermissions([PERMISSIONS.OBJECT.CREATE, PERMISSIONS.PROJECT.EDIT])

    return <statusContext.Provider value={state}>
        <inactiveContext.Provider value={isArchivedProject(project.status_id)}>
            {
                state === RequestStatus.FETCHING && <TopBarProgress/>
            }
            <Helmet>
                <title>{project.code} Проект</title>
            </Helmet>
            <div className={styles.page}>
                <div ref={mainRef} className={styles.main}>
                    <ProjectInfo className={styles.info} project={project}>
                        <HasAnyPermission condition={!objectEdit} permissions={[PERMISSIONS.OBJECT.CREATE, PERMISSIONS.PROJECT.EDIT]}>
                            <Button
                                color={Colors.blue}
                                buttonStyle={ButtonStyle.BORDERED}
                                justify={'space-evenly'}
                                onClick={() => toggleObjects(!objectEdit)}
                                title={'Добавить объект'}
                            >
                                <PlusIcon/>
                            </Button>
                        </HasAnyPermission>
                    </ProjectInfo>
                    {
                        objectEdit && <ObjectPicker
                            objects={projectWorkObjects}
                            onToggle={toggleObjects}
                            onChange={objects => dispatch(projectActions.update.request(
                                {
                                    id: projectId,
                                    project_work_objects: objects.concat(projectWorkObjects),
                                },
                                {}
                            ))}
                        />
                    }
                    {
                        state !== RequestStatus.FETCHING && isEmpty(projectWorkObjects) && !objectEdit ?
                            isPermit ?
                                <NotFound
                                    headerText='В проекте пока нет объектов.'
                                    imageName='EMPTY_3'
                                    actions={<Button
                                        color={Colors.blue}
                                        buttonStyle={ButtonStyle.WHITE_BORDERED}
                                        justify={'space-evenly'}
                                        onClick={() => toggleObjects(!objectEdit)}
                                    >
                                        <PlusIcon/> Добавить объект
                                    </Button>}
                                /> :
                                <NotFound
                                    headerText='В проекте пока нет объектов.'
                                    infoText='Обратитесь к руководителю, чтобы он
                                    добавил объекты и поставил на вас задачу.'
                                    imageName='EMPTY_3'
                                /> 
                            : ''

                    }
                    <LoadingSkeleton count={5} height={90} className={styles.list}>
                        {
                            projectWorkObjects.map(object => <div key={object.id}>
                                <ObjectHeader
                                    objectId={object.id}
                                    active={openObjects[object.id]}
                                    onClick={() => dispatch(projectDetailActions.toggleObject(object.id))}
                                />
                                <ObjectBody objectId={object.id}/>
                            </div>)
                        }
                    </LoadingSkeleton>
                </div>
                {
                    isDesktop && <Participants project={project}/>
                }
                {
                    ObjectEditState.is.close(objectEditState) && <ObjectCloseDialog
                        object={objectEditState.object}
                        onClose={flow(objectActions.close.request, dispatch)}
                        onOpen={flow(objectActions.update.request, dispatch)}
                        onCancel={() => dispatch(objectActions.cancelEdit())}
                    />
                }
            </div>
        </inactiveContext.Provider>
    </statusContext.Provider>
}

export default ProjectPage